import { type ReactElement, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { useEntityDetailPageScaffold } from '@/features/shared/hooks/useEntityDetailPageScaffold';
import { useDeleteSuite, useSuiteDetailQuery } from '@/features/suites/hooks/suites';
import { useSuiteDetailPageViewModel } from '@/features/suites/hooks/useSuiteDetailPageViewModel';
import { useProjectContextGuard } from '@/shared/hooks/useProjectContextGuard';
import { DetailPageScaffold } from '@/ui/composites/scaffolds';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { SuiteDetailContent } from './SuiteDetailContent';
import type { AuthSession } from '@/auth/types';

type SuiteDetailPageProps = { session: AuthSession | null };

export const SuiteDetailPage = ({ session }: SuiteDetailPageProps): ReactElement => {
  const { slug } = useParams<{ slug: string }>();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const deleteSuite = useDeleteSuite();
  const { setOverrideItems, clearOverride } = useBreadcrumbs();
  const suiteQuery = useSuiteDetailQuery(slug ?? null, selectedProjectId ?? null);
  const { data: suite } = suiteQuery;
  const { isPending: isSuitePending, isError: isSuiteError, error } = suiteQuery;

  useProjectContextGuard({
    entity: suite,
    isPending: suiteQuery.isPending,
    isFetched: suiteQuery.isFetched,
    selectedProjectId,
    setSelectedProjectId,
    syncContextOnMismatch: true,
    listPath: '/suites',
  });

  const detailPage = useEntityDetailPageScaffold({
    load: {
      data: suite,
      isPending: isSuitePending,
      isFetched: suiteQuery.isFetched,
    },
    delete: {
      mutation: deleteSuite,
      successMessage: 'Suite deleted.',
      fallbackErrorMessage: 'Failed to delete suite.',
    },
    redirects: {
      onDeleteTo: '/suites',
      notFoundBackLabel: 'Back to suites',
    },
    notFoundCopy: {
      title: 'Suite not found',
      body: 'This suite does not exist or you no longer have access to it.',
    },
  });
  const pageViewModel = useSuiteDetailPageViewModel({ suite });

  useEffect(() => {
    if (!suite) return;
    setOverrideItems([{ label: 'Suites', href: '/suites' }, { label: suite.name }]);
  }, [setOverrideItems, suite?.id, suite?.name]);

  useEffect(
    () => () => {
      clearOverride();
    },
    [clearOverride],
  );

  if (detailPage.showNotFound) return detailPage.renderNotFound();

  return (
    <DetailPageScaffold
      session={session}
      sessionMessage="Sign in to view suite details."
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      isPending={isSuitePending}
      isError={isSuiteError}
      error={error}
      errorMessage="Failed to load suite"
      data={suite}
      loadingFallback={<></>}
    >
      {(resolvedSuite) => {
        return (
          <SuiteDetailContent
            suite={resolvedSuite}
            viewModel={pageViewModel}
            onDelete={detailPage.handleDelete}
            deleteLoading={detailPage.deleteLoading}
          />
        );
      }}
    </DetailPageScaffold>
  );
};
